/**
 * PaymentLinkModal — genera un link de pago para un documento vía una pasarela
 * activa. Extraído de SaleDocumentsPage para reuso (lista + Vista 360).
 */
import { useState } from 'react'
import { Link, CheckCircle2, Copy, Loader2 } from 'lucide-react'
import { useCreatePaymentLink, usePaymentGateways } from '@/modules/payments/hooks/usePayments'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

export function PaymentLinkModal({ docId, onClose }: { docId: string; onClose: () => void }) {
  const { data: gateways = [] } = usePaymentGateways()
  const { mutate: createLink, isPending, data: linkResult, isSuccess } = useCreatePaymentLink()
  const [selectedGateway, setSelectedGateway] = useState('')
  const [copied, setCopied] = useState(false)

  const activeGateways = gateways.filter(g => g.is_active && g.has_credentials)

  const handleCreate = () => {
    if (!selectedGateway) return
    createLink({ document_id: docId, gateway_type: selectedGateway })
  }

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Link size={15} className="text-primary" /> Generar link de pago</span>} size="2xl">
      {!isSuccess ? (
        <>
          {activeGateways.length === 0 ? (
            <EmptyState
              title="No hay pasarelas activas con credenciales configuradas."
              hint="Se activan en Configuración → Pasarelas de Pago."
              size="sm"
            />
          ) : (
            <>
              <div className="mb-4">
                <label htmlFor="paymentlinkmodal-pasarela-de-pago" className="block text-xs font-medium text-content-muted mb-1">Pasarela de pago</label>
                <select id="paymentlinkmodal-pasarela-de-pago"
                  value={selectedGateway}
                  onChange={e => setSelectedGateway(e.target.value)}
                  className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
                >
                  <option value="">Seleccionar pasarela...</option>
                  {activeGateways.map(g => (
                    <option key={g.gateway_type} value={g.gateway_type}>
                      {g.gateway_type_display} {g.is_sandbox ? '(Sandbox)' : '(Producción)'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
                  Cancelar
                </button>
                <button
                  onClick={handleCreate}
                  disabled={isPending || !selectedGateway}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
                >
                  {isPending && <Loader2 size={13} className="animate-spin" />}
                  Generar link
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-success-content-soft mb-2">
            <CheckCircle2 size={16} />
            <span className="text-sm font-medium">Link generado correctamente</span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-glass-3 rounded-lg border border-divider">
            <span className="text-xs text-content-muted flex-1 truncate font-mono">
              {(linkResult as { url?: string })?.url || ''}
            </span>
            <button
              onClick={() => handleCopy((linkResult as { url?: string })?.url || '')}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover shrink-0"
            >
              {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          {(linkResult as { url?: string })?.url && (
            <a
              href={(linkResult as { url?: string }).url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-xs text-primary hover:underline"
            >
              Abrir link de pago
            </a>
          )}
          <button onClick={onClose} className="w-full mt-2 px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
            Cerrar
          </button>
        </div>
      )}
    </Modal>
  )
}
